// Theme Version Edit Page with Advanced Editor Components
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DesktopLayout from '../../../../components/layout/DesktopLayout';
import { ColorPicker, TypographyEditor, SpacingEditor, ShadowsEditor, BrandEditor, ThemeImportExport, ThemePreviewModal } from '../../../../components/admin/theme';
import type { ThemeConfig } from '../../../../types/theme';
import { getThemeVersion, createThemeVersion, updateThemeVersion, updateThemeConfig } from '../../../../services/themeApi';
import { DEFAULT_DESKTOP_THEME, DEFAULT_MOBILE_THEME } from '../../../../config/defaultTheme';
import './index.css';

const ThemeVersionPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [version, setVersion] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'desktop' | 'mobile'>('desktop');
  const [section, setSection] = useState<'metadata' | 'colors' | 'typography' | 'spacing' | 'shadows' | 'brand'>('metadata');
  const [showPreview, setShowPreview] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);

  useEffect(() => {
    if (id === 'new') {
      // Create new version with defaults from shared config
      setVersion({
        id: '',
        name: '新主題',
        description: '',
        isActive: false,
        createdAt: new Date().toISOString(),
        createdBy: '',
        desktop: { ...DEFAULT_DESKTOP_THEME },
        mobile: { ...DEFAULT_MOBILE_THEME },
      });
      setIsLoading(false);
    } else {
      loadVersion();
    }
  }, [id]);

  const loadVersion = async () => {
    try {
      setIsLoading(true);
      const data = await getThemeVersion(id!);
      setVersion(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load theme version';
      alert(message);
      navigate('/admin/theme');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!version) return;

    try {
      setIsSaving(true);

      if (id === 'new') {
        const newId = version.id || `v${Date.now()}`;
        await createThemeVersion({
          id: newId,
          name: version.name,
          description: version.description,
        });
        navigate(`/admin/theme/version/${newId}`);
      } else {
        if (!id) return;
        await updateThemeVersion(id, {
          name: version.name,
          description: version.description,
        });
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save theme version';
      alert(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!version || id === 'new' || !id) return;

    // Ensure activeTab is properly typed
    const device: 'desktop' | 'mobile' = activeTab === 'desktop' || activeTab === 'mobile' ? activeTab : 'desktop';

    try {
      setIsSaving(true);
      await updateThemeConfig(id, device, version[device]);
      alert('主題配置已儲存');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save theme config';
      alert(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSharePreview = async () => {
    if (!version || id === 'new' || !id) return;

    try {
      setIsGeneratingLink(true);

      const apiUrl = import.meta.env.VITE_API_URL || '';
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${apiUrl}/api/themes/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          versionId: id,
          deviceType: activeTab,
          ttl: 3600, // 1 hour
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate preview link');
      }

      const result = await response.json();
      if (result.success && result.data) {
        const baseUrl = window.location.origin;
        setShareUrl(`${baseUrl}${result.data.previewUrl}`);
        setShowShareModal(true);
      } else {
        throw new Error(result.error?.message || 'Failed to generate preview link');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to generate preview link';
      alert(`生成預覽連結失敗: ${message}`);
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const copyShareUrl = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl);
      alert('預覽連結已複製到剪貼簿');
    }
  };

  const updateTheme = (updates: Partial<ThemeConfig>) => {
    if (!version) return;
    setVersion({
      ...version,
      [activeTab]: {
        ...version[activeTab],
        ...updates,
      },
    });
  };

  const handleImport = (config: any) => {
    if (!version) return;
    setVersion({
      ...version,
      [activeTab]: config,
    });
    alert('主題配置已匯入');
  };

  if (isLoading) {
    return (
      <div className="theme-version-page">
        <DesktopLayout>
          <main className="theme-version-page__main">
            <div className="theme-version-page__loading">載入中...</div>
          </main>
        </DesktopLayout>
      </div>
    );
  }

  if (!version) {
    return null;
  }

  const currentTheme = version[activeTab];
  const defaultTheme = activeTab === 'desktop' ? DEFAULT_DESKTOP_THEME : DEFAULT_MOBILE_THEME;

  // Parse numeric values from theme config
  const parseNumericValue = (value: string | number, defaultValue: number): number => {
    if (typeof value === 'number') return value;
    const parsed = parseFloat(String(value).replace(/[^\d.]/g, ''));
    return isNaN(parsed) ? defaultValue : parsed;
  };

  const fontSize = parseNumericValue(currentTheme.typography.fontSizeBase, 16);
  const lineHeight = currentTheme.typography.lineHeight || 1.5;
  const baseUnit = parseNumericValue(currentTheme.spacing.baseUnit, activeTab === 'desktop' ? 16 : 4);
  const borderRadius = parseNumericValue(currentTheme.spacing.borderRadiusMedium, 12);
  const headerHeight = parseNumericValue(currentTheme.layout.headerHeight, activeTab === 'desktop' ? 44 : 56);
  const maxContentWidth = parseNumericValue(currentTheme.layout.maxContentWidth, activeTab === 'desktop' ? 1400 : 100);

  return (
    <div className="theme-version-page">
      <DesktopLayout>
        <main className="theme-version-page__main">
          <div className="theme-version-page__container">
            {/* Header */}
            <header className="theme-version-page__header">
              <div>
                <button onClick={() => navigate('/admin/theme')} className="theme-version-page__back-btn">
                  ← 返回列表
                </button>
                <h1 className="theme-version-page__title">
                  {id === 'new' ? '建立新主題' : version.name}
                </h1>
              </div>
              <div className="theme-version-page__actions">
                <button
                  onClick={() => setShowPreview(true)}
                  className="theme-version-page__preview-btn"
                >
                  👁️ 預覽
                </button>
                {id !== 'new' && (
                  <button
                    onClick={handleSharePreview}
                    disabled={isGeneratingLink}
                    className="theme-version-page__share-btn"
                  >
                    {isGeneratingLink ? '生成中...' : '🔗 分享預覽'}
                  </button>
                )}
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="theme-version-page__save-btn"
                >
                  {isSaving ? '儲存中...' : '儲存資訊'}
                </button>
                {id !== 'new' && (
                  <button
                    onClick={handleSaveConfig}
                    disabled={isSaving}
                    className="theme-version-page__save-config-btn"
                  >
                    {isSaving ? '儲存中...' : '儲存配置'}
                  </button>
                )}
              </div>
            </header>

            {/* Import/Export */}
            {id !== 'new' && (
              <ThemeImportExport
                themeConfig={currentTheme}
                onImport={handleImport}
                versionId={id}
              />
            )}

            {/* Version Info */}
            <section className="theme-version-page__section">
              <h2 className="theme-version-page__section-title">版本資訊</h2>
              <div className="theme-version-page__form-group">
                <label>版本 ID</label>
                <input
                  type="text"
                  value={version.id}
                  onChange={(e) => setVersion({ ...version, id: e.target.value })}
                  disabled={id !== 'new'}
                  placeholder="v1"
                />
              </div>
              <div className="theme-version-page__form-group">
                <label>名稱</label>
                <input
                  type="text"
                  value={version.name}
                  onChange={(e) => setVersion({ ...version, name: e.target.value })}
                  placeholder="主題名稱"
                />
              </div>
              <div className="theme-version-page__form-group">
                <label>描述</label>
                <textarea
                  value={version.description || ''}
                  onChange={(e) => setVersion({ ...version, description: e.target.value })}
                  placeholder="主題描述"
                  rows={3}
                />
              </div>
            </section>

            {/* Device Tabs */}
            <div className="theme-version-page__tabs">
              <button
                className={`theme-version-page__tab ${activeTab === 'desktop' ? 'active' : ''}`}
                onClick={() => setActiveTab('desktop')}
              >
                桌面版
              </button>
              <button
                className={`theme-version-page__tab ${activeTab === 'mobile' ? 'active' : ''}`}
                onClick={() => setActiveTab('mobile')}
              >
                手機版
              </button>
            </div>

            {/* Section Tabs */}
            <div className="theme-version-page__section-tabs">
              <button
                className={`theme-version-page__section-tab ${section === 'metadata' ? 'active' : ''}`}
                onClick={() => setSection('metadata')}
              >
                基本設定
              </button>
              <button
                className={`theme-version-page__section-tab ${section === 'colors' ? 'active' : ''}`}
                onClick={() => setSection('colors')}
              >
                顏色
              </button>
              <button
                className={`theme-version-page__section-tab ${section === 'typography' ? 'active' : ''}`}
                onClick={() => setSection('typography')}
              >
                字體
              </button>
              <button
                className={`theme-version-page__section-tab ${section === 'spacing' ? 'active' : ''}`}
                onClick={() => setSection('spacing')}
              >
                間距
              </button>
              <button
                className={`theme-version-page__section-tab ${section === 'brand' ? 'active' : ''}`}
                onClick={() => setSection('brand')}
              >
                品牌
              </button>
              <button
                className={`theme-version-page__section-tab ${section === 'shadows' ? 'active' : ''}`}
                onClick={() => setSection('shadows')}
              >
                陰影
              </button>
            </div>

            {/* Content */}
            <section className="theme-version-page__content">
              {section === 'metadata' && (
                <SpacingEditor
                  baseUnit={baseUnit}
                  borderRadius={borderRadius}
                  headerHeight={headerHeight}
                  maxContentWidth={maxContentWidth}
                  onChange={(field, value) => {
                    if (field === 'baseUnit') {
                      updateTheme({ spacing: { ...currentTheme.spacing, baseUnit: `${value}px` } });
                    } else if (field === 'borderRadius') {
                      updateTheme({
                        spacing: {
                          ...currentTheme.spacing,
                          borderRadiusSmall: `${value}px`,
                          borderRadiusMedium: `${value}px`,
                          borderRadiusLarge: `${value * 1.5}px`,
                        }
                      });
                    } else if (field === 'headerHeight') {
                      updateTheme({ layout: { ...currentTheme.layout, headerHeight: `${value}px` } });
                    } else if (field === 'maxContentWidth') {
                      updateTheme({ layout: { ...currentTheme.layout, maxContentWidth: `${value}px` } });
                    }
                  }}
                  defaults={{
                    baseUnit: parseNumericValue(defaultTheme.spacing.baseUnit, 16),
                    borderRadius: parseNumericValue(defaultTheme.spacing.borderRadiusMedium, 12),
                    headerHeight: parseNumericValue(defaultTheme.layout.headerHeight, 44),
                    maxContentWidth: parseNumericValue(defaultTheme.layout.maxContentWidth, 1400),
                  }}
                />
              )}

              {section === 'colors' && (
                <div className="theme-version-page__form-grid">
                  <ColorPicker
                    label="Primary"
                    value={currentTheme.colors.primary}
                    onChange={(value) => updateTheme({ colors: { ...currentTheme.colors, primary: value } })}
                    defaultValue={defaultTheme.colors.primary}
                  />
                  <ColorPicker
                    label="Primary Hover"
                    value={currentTheme.colors.primaryHover}
                    onChange={(value) => updateTheme({ colors: { ...currentTheme.colors, primaryHover: value } })}
                    defaultValue={defaultTheme.colors.primaryHover}
                  />
                  <ColorPicker
                    label="Primary Dark"
                    value={currentTheme.colors.primaryDark}
                    onChange={(value) => updateTheme({ colors: { ...currentTheme.colors, primaryDark: value } })}
                    defaultValue={defaultTheme.colors.primaryDark}
                  />
                  <ColorPicker
                    label="Background Primary"
                    value={currentTheme.colors.bgPrimary}
                    onChange={(value) => updateTheme({ colors: { ...currentTheme.colors, bgPrimary: value } })}
                    defaultValue={defaultTheme.colors.bgPrimary}
                  />
                  <ColorPicker
                    label="Background Secondary"
                    value={currentTheme.colors.bgSecondary}
                    onChange={(value) => updateTheme({ colors: { ...currentTheme.colors, bgSecondary: value } })}
                    defaultValue={defaultTheme.colors.bgSecondary}
                  />
                  <ColorPicker
                    label="Text Primary"
                    value={currentTheme.colors.textPrimary}
                    onChange={(value) => updateTheme({ colors: { ...currentTheme.colors, textPrimary: value } })}
                    defaultValue={defaultTheme.colors.textPrimary}
                  />
                  <ColorPicker
                    label="Text On Dark"
                    value={currentTheme.colors.textOnDark}
                    onChange={(value) => updateTheme({ colors: { ...currentTheme.colors, textOnDark: value } })}
                    defaultValue={defaultTheme.colors.textOnDark}
                  />
                  <ColorPicker
                    label="Border Primary"
                    value={currentTheme.colors.borderPrimary}
                    onChange={(value) => updateTheme({ colors: { ...currentTheme.colors, borderPrimary: value } })}
                    defaultValue={defaultTheme.colors.borderPrimary}
                  />
                </div>
              )}

              {section === 'typography' && (
                <TypographyEditor
                  fontFamily={currentTheme.typography.fontFamily}
                  fontSize={fontSize}
                  lineHeight={lineHeight}
                  fontWeight={400}
                  onChange={(field, value) => {
                    if (field === 'fontFamily') {
                      updateTheme({ typography: { ...currentTheme.typography, fontFamily: String(value) } });
                    } else if (field === 'fontSize') {
                      updateTheme({ typography: { ...currentTheme.typography, fontSizeBase: `${value}px` } });
                    } else if (field === 'lineHeight') {
                      updateTheme({ typography: { ...currentTheme.typography, lineHeight: Number(value) } });
                    } else if (field === 'fontWeight') {
                      // Font weight not currently in theme config, but can be added
                    }
                  }}
                  defaults={{
                    fontFamily: defaultTheme.typography.fontFamily,
                    fontSize: parseNumericValue(defaultTheme.typography.fontSizeBase, 16),
                    lineHeight: defaultTheme.typography.lineHeight || 1.5,
                    fontWeight: 400,
                  }}
                />
              )}

              {section === 'spacing' && (
                <SpacingEditor
                  baseUnit={baseUnit}
                  borderRadius={borderRadius}
                  headerHeight={headerHeight}
                  maxContentWidth={maxContentWidth}
                  onChange={(field, value) => {
                    if (field === 'baseUnit') {
                      updateTheme({ spacing: { ...currentTheme.spacing, baseUnit: `${value}px` } });
                    } else if (field === 'borderRadius') {
                      updateTheme({
                        spacing: {
                          ...currentTheme.spacing,
                          borderRadiusSmall: `${value}px`,
                          borderRadiusMedium: `${value}px`,
                          borderRadiusLarge: `${value * 1.5}px`,
                        }
                      });
                    } else if (field === 'headerHeight') {
                      updateTheme({ layout: { ...currentTheme.layout, headerHeight: `${value}px` } });
                    } else if (field === 'maxContentWidth') {
                      updateTheme({ layout: { ...currentTheme.layout, maxContentWidth: `${value}px` } });
                    }
                  }}
                  defaults={{
                    baseUnit: parseNumericValue(defaultTheme.spacing.baseUnit, 16),
                    borderRadius: parseNumericValue(defaultTheme.spacing.borderRadiusMedium, 12),
                    headerHeight: parseNumericValue(defaultTheme.layout.headerHeight, 44),
                    maxContentWidth: parseNumericValue(defaultTheme.layout.maxContentWidth, 1400),
                  }}
                />
              )}

              {section === 'brand' && (
                <BrandEditor
                  siteName={currentTheme.brand.siteName}
                  logoUrl={currentTheme.brand.logoUrl}
                  faviconUrl={currentTheme.brand.faviconUrl}
                  onChange={(field, value) => {
                    updateTheme({ brand: { ...currentTheme.brand, [field]: value } });
                  }}
                />
              )}

              {section === 'shadows' && (
                <ShadowsEditor
                  primary={currentTheme.shadows.primary}
                  primaryHover={currentTheme.shadows.primaryHover}
                  card={currentTheme.shadows.card}
                  cardHover={currentTheme.shadows.cardHover}
                  header={currentTheme.shadows.header}
                  onChange={(field, value) => {
                    updateTheme({ shadows: { ...currentTheme.shadows, [field]: value } });
                  }}
                  defaults={{
                    primary: defaultTheme.shadows.primary,
                    primaryHover: defaultTheme.shadows.primaryHover,
                    card: defaultTheme.shadows.card,
                    cardHover: defaultTheme.shadows.cardHover,
                    header: defaultTheme.shadows.header,
                  }}
                />
              )}
            </section>
          </div>
        </main>
      </DesktopLayout>

      {/* Preview Modal */}
      <ThemePreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        themeConfig={currentTheme}
        device={activeTab}
      />

      {/* Share Preview Modal */}
      {showShareModal && shareUrl && (
        <div className="share-modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="share-modal" onClick={(e) => e.stopPropagation()}>
            <div className="share-modal-header">
              <h3>分享主題預覽</h3>
              <button onClick={() => setShowShareModal(false)} className="share-modal-close">✕</button>
            </div>
            <div className="share-modal-body">
              <p>任何人都可以使用此連結預覽主題，無需登入。</p>
              <div className="share-url-container">
                <input
                  type="text"
                  readOnly
                  value={shareUrl}
                  className="share-url-input"
                />
                <button onClick={copyShareUrl} className="share-copy-btn">
                  📋 複製
                </button>
              </div>
              <p className="share-expiry">⏰ 連結將於 1 小時後過期</p>
            </div>
            <div className="share-modal-footer">
              <button onClick={() => setShowShareModal(false)} className="share-modal-btn">
                關閉
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThemeVersionPage;
